import { ActionDialog, ActionsMoreInfoDialog } from '@communities/content-management/components';
import type { SafetyAction, EntityType } from '@communities/content-management/domain';
import { useCommunityActions, useDeleteCommunityAction, useDeleteCommunityGroupAction, useCommunityPolicies } from '@communities/content-management/hooks';
import { useCommunityGroupActions } from '@communities/content-management/hooks/useCommunityGroupActions';
import { getEntityDisplayName, isCommunity } from '@communities/content-management/utils';
import { DataList, type DeleteItemContext, type TableColumn } from '@core/components';
import { SectionHeader } from '@core/components';
import { withAppInsightsTracking } from '@core/logging/withAppInsightsTracking';
import { Button, Badge, IconButton, Info16, IconButtonSize, IconSize, Section } from '@soteria/design-system-ve';
import { useState, useCallback, useMemo } from 'react';

interface ActionsTabProps {
  entityId: string;
  entityType: EntityType;
  isEntityAdmin: boolean;
  sectionTitle?: string;
}

// eslint-disable-next-line complexity
function ActionsTabComponent({ entityId, entityType, isEntityAdmin, sectionTitle }: ActionsTabProps) {
  const [selectedAction, setSelectedAction] = useState<SafetyAction | undefined>(undefined);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isMoreInfoModalOpen, setIsMoreInfoModalOpen] = useState(false);

  // Hooks for getting actions
  const communityHook = useCommunityActions(isCommunity(entityType) ? entityId : undefined);
  const communityGroupHook = useCommunityGroupActions(!isCommunity(entityType) ? entityId : undefined);

  // Hooks for deleting actions
  const communityDeleteActionHook = useDeleteCommunityAction(isCommunity(entityType) ? entityId : undefined);
  const communityGroupDeleteActionHook = useDeleteCommunityGroupAction(!isCommunity(entityType) ? entityId : undefined);

  // Hook for getting policies (to display policy names)
  const { policies } = useCommunityPolicies(isCommunity(entityType) ? entityId : undefined);

  const { actions, loading, errors } = isCommunity(entityType) ? communityHook : communityGroupHook;
  const {
    deleteAction,
    loading: isDeleting,
    errors: deleteErrors,
    resetErrors,
  } = isCommunity(entityType) ? communityDeleteActionHook : communityGroupDeleteActionHook;

  // Create a lookup map from policy ID to policy name
  const policyNameMap = useMemo(() => {
    const map = new Map<string, string>();

    policies.forEach(policy => {
      map.set(policy.id, policy.name);
    });

    return map;
  }, [policies]);

  const columns: TableColumn<SafetyAction>[] = [
    {
      key: 'name',
      header: 'Name',
      render: action => action.name,
    },
    {
      key: 'description',
      header: 'Description',
      render: action => action.description || '-',
    },
    {
      key: 'policies',
      header: 'Policies',
      render: action => renderPolicies(action),
    },
  ];

  const renderPolicies = (action: SafetyAction) => {
    if (!action.policies || action.policies.length === 0) {
      return '-';
    }

    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
        {action.policies.map(policyId => {
          const policyName = policyNameMap.get(policyId) || policyId;

          return <Badge key={policyId} text={policyName} />;
        })}
      </div>
    );
  };

  const handleOpenDialog = useCallback((action?: SafetyAction) => {
    setSelectedAction(action || undefined);
    setIsDialogOpen(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setIsDialogOpen(false);
    setSelectedAction(undefined);
  }, []);

  // Create the delete item context
  const deleteItemContext: DeleteItemContext<SafetyAction> | undefined = isEntityAdmin
    ? {
        handler: async (item: SafetyAction) => await deleteAction(entityId, item.name),
        isDeleting,
        errors: deleteErrors,
        resetErrors,
      }
    : undefined;

  return (
    <>
      <Section spacing="large">
        <SectionHeader
          description={`Configure actions for this ${getEntityDisplayName(entityType)}.`}
          endComponent={
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <IconButton
                aria-label="Learn more about actions"
                buttonSize={IconButtonSize.Medium}
                icon={Info16}
                iconSize={IconSize.XSmall}
                onClick={() => setIsMoreInfoModalOpen(true)}
              />
              {isEntityAdmin && <Button onClick={() => handleOpenDialog()}>ADD ACTION</Button>}
            </div>
          }
          title={sectionTitle || `Actions (${actions.length})`}
        />

        {/* Actions List*/}
        <DataList
          columns={columns}
          data={actions}
          dataTypeName="action"
          deleteItemContext={deleteItemContext}
          error={errors}
          isLoading={loading}
          onEdit={isEntityAdmin ? handleOpenDialog : undefined}
        />
      </Section>

      {/* Create/Edit Action Dialog */}
      <ActionDialog action={selectedAction} entityId={entityId} entityType={entityType} isOpen={isDialogOpen} onClose={handleCloseDialog} />

      {/* More Info Dialog */}
      <ActionsMoreInfoDialog isOpen={isMoreInfoModalOpen} onClose={() => setIsMoreInfoModalOpen(false)} />
    </>
  );
}

export const ActionsTab = withAppInsightsTracking(ActionsTabComponent, 'ActionsTab');
